const ADAPTER_ENABLED = true;

export function wrapAdapter(instance) {
  return new Proxy(instance, {
    get(target, prop) {
      const value = target[prop];

      // If it's an object (nested adapter), wrap it again
      if (typeof value === "object" && value !== null) {
        return wrapAdapter(value);
      }

      // If it's not a function, return it normally
      if (typeof value !== "function") {
        return value;
      }

      // Wrap function
      return (...args) => {
        if (!ADAPTER_ENABLED) {
          // Return first argument (your input)
          return args[0];
        }

        return value.apply(target, args);
      };
    },
  });
}

const lvOldNewStatusMap = {
  created: "uploaded",
  in_process: "processing",
  done: "completed",
  error: "failed",
};

class AdminAdapter {
  dashboard(data) {
    return {
      ...data,
      recentActivity: {
        ...data.recentActivity,
        users: data.recentActivity.users.map((user) => ({
          ...user,
          _id: user.id,
          name: user.firstName + " " + (user.lastName || ""),
        })),
        organizations: data.recentActivity.organizations.map(
          (organization) => ({
            ...organization,
            _id: organization.id,
          }),
        ),
        projects: data.recentActivity.projects.map((project) => ({
          ...project,
          _id: project.id,
          title: project.name,
        })),
      },
    };
  }
  listOrganizations(data) {
    return {
      ...data,
      organizations: data.data.map((organization) => ({
        ...organization,
        _id: organization.id,
      })),
    };
  }
  listProjects(data) {
    return {
      ...data,
      projects: data.data.map((project) => ({
        ...project,
        _id: project.id,
        title: project.name,
        organization: {
          _id: project.organization_id,
          name: project.organizationName,
        },
        owner: {
          ...project.owner,
          _id: project.owner.id,
          name: project.owner.firstName + " " + (project.owner.lastName || ""),
        },
        lastUpdated: project.updatedAt,
      })),
    };
  }
  listUsers(data) {
    return {
      ...data,
      users: data.data.map((user) => ({
        ...user,
        _id: user.id,
        name: user.firstName + " " + (user.lastName || ""),
        organization: user.organizationId && {
          _id: user.organizationId,
          name: user.organizationName,
        },
      })),
    };
  }
  createOrganization(data) {
    return {
      name: data.name,
      description: data.description,
      initialAdminId: +data.initialAdminId,
      organizationType: data.type,
    };
  }
}

class UserAdapter {
  me(data) {
    return {
      ...data,
      isGlobalAdmin: data.role === "super_admin",
      name: data.firstName + " " + (data.lastName || ""),
      organizationId:
        (data.organization && data.organization.id) ||
        data.organizationId ||
        null,
      organizationType:
        (data.organization && data.organization.type) ||
        data.organizationType ||
        null,
      organizationName:
        (data.organization && data.organization.name) ||
        data.organizationName ||
        null,
    };
  }
}

class ProjectAdapter {
  list(data) {
    return {
      ...data,
      projects: data.projects.map((project) => ({
        _id: project.id,
        title: project.name,
        image: "/images/demo-projects/default.png", // Use project image if available
        description: project.description,
        status: project.status,
        createdAt: project.createdAt,
        archivedAt: project.archivedAt,
        organizationId: project.organizationId,
        // A project without an organization comes with organization: null
        organizationName: project.organization?.name ?? null,
      })),
    };
  }
  getProject(data) {
    return {
      ...data,
      project: {
        ...data.project,
        _id: data.project.id,
        title: data.project.name,
        pdsProjectId: data.project.pdsProjectId || null,
      },
    };
  }
  getTeam(data) {
    return {
      ...data,
      teamMembers: data.teamMembers.map((member) => ({
        ...member,
        _id: member.id,
        name: member.firstName + " " + (member.lastName || ""),
      })),
    };
  }
  getAvailableMembers(data) {
    return data.map((member) => ({
      ...member,
      _id: member.id,
      name: member.firstName + " " + (member.lastName || ""),
    }));
  }
  addTeamMember(data) {
    return {
      userId: +data.userId,
      role: data.role,
    };
  }
}

class SectionAdapter {
  create(data) {
    return {
      section: {
        _id: data.id.toString(),
        name: data.name,
        description: data.description,
      },
    };
  }
  list(data) {
    const toStatus = (status) => {
      switch (status) {
        case "in_process":
          return "processing";
        case "done":
          return "processed";
        case "error":
          return "failed";
        default:
          return "created";
      }
    };
    return {
      sections: data.sections.map((section) => ({
        ...section,
        _id: section.id.toString(),
        ...(section.plan && {
          plan: {
            ...section.plan,
            planId: section.plan.id,
            uploadDate: section.plan.updatedAt,
            hasPreview: !!section.plan.previewUrl,
            status: toStatus(section.plan.status),
          },
        }),
      })),
    };
  }
  getPlan(data) {
    const toStatus = (status) => {
      switch (status) {
        case "in_process":
          return "processing";
        case "done":
          return "processed";
        case "error":
          return "failed";
        default:
          return "created";
      }
    };
    return {
      section: {
        ...data.section,
        _id: data.section.id.toString(),
        project: {
          _id: data.section.projectId,
        },
        ...(data.section.plan && {
          plan: {
            ...data.section.plan,
            planId: data.section.plan.id,
            uploadDate: data.section.plan.updatedAt,
            hasPreview: !!data.section.plan.previewUrl,
            status: toStatus(data.section.plan.status),
          },
        }),
      },
    };
  }
  uploadPlanResponse(data) {
    const toStatus = (status) => {
      switch (status) {
        case "in_process":
          return "processing";
        case "done":
          return "processed";
        case "error":
          return "failed";
        default:
          return "created";
      }
    };
    return {
      ...data,
      section: {
        ...data.section,
        _id: data.section.id.toString(),
        ...(data.section.plan && {
          plan: {
            ...data.section.plan,
            planId: data.section.plan.id,
            uploadDate: data.section.plan.updatedAt,
            hasPreview: !!data.section.plan.previewUrl,
            status: toStatus(data.section.plan.status),
          },
        }),
      },
    };
  }
}

class LvAdapter {
  getLV(data) {
    return {
      ...data,
      _id: data.id,
      items: data.items.map((item) => ({
        ...item,
        _id: item.id,
      })),
      processingStatus: lvOldNewStatusMap[data.status],
    };
  }
  addItem(data) {
    return {
      position: data.id,
      name: data.name,
      quantity: data.quantity,
      unit: data.unit,
    };
  }
}

class DocumentationAdapter {
  list(data) {
    return {
      documentation: data.documentations.map((documentation) => ({
        ...documentation,
        _id: documentation.id,
        ...(documentation.frames && {
          frames: documentation.frames.map((frame) => ({
            ...frame,
            _id: frame.id,
            objects: frame.objects.map((object) => ({
              ...object,
              bndbox: object.bbox,
              detectedObjectId: {
                _id: object.elementId,
                type: object.type || object.groupName,
                status:
                  object.elementSource === "plan"
                    ? "installed"
                    : "plan_vs_real_dev",
                position: object.position,
                chessboard_id:
                  object.gridCols && object.gridRows
                    ? `[${object.gridCols.join(",")}],[${object.gridRows.join("-")}]`
                    : null,
              },
            })),
          })),
        }),
      })),
      pagination: {
        current: data.page,
        total: data.total,
        count: data.count,
      },
    };
  }
  get(data) {
    return {
      ...data,
      _id: data.id,
      projectId: { ...data.project, _id: data.project.id },
      sectionId: { ...data.section, _id: data.section.id },
      frames: data.frames.map((frame) => ({
        ...frame,
        _id: frame.id,
        objects: frame.objects.map((object) => ({
          ...object,
          bndbox: object.bbox,
          detectedObjectId: {
            _id: object.elementId,
            type: object.type || object.groupName,
            status:
              object.elementSource === "plan"
                ? "installed"
                : "plan_vs_real_dev",
            position: object.position,
            chessboard_id:
              object.gridCols && object.gridRows
                ? `[${object.gridCols.join(",")}],[${object.gridRows.join("-")}]`
                : null,
          },
        })),
      })),
    };
  }
  addFrameObject(data) {
    return {
      isDeviation: data.isDeviation,
      elementId: data.detectedObjectId,
      bndbox: data.bndbox,
      isCableInstalled: data.isCableInstalled,
    };
  }
  initiateVideoUpload(data) {
    const { sectionId, projectId, ...rest } = data;
    return {
      ...rest,
      sectionId: +sectionId,
      projectId: +projectId,
    };
  }
  completeVideoUpload(data) {
    const { sectionId, projectId, ...rest } = data;
    return {
      ...rest,
      sectionId: +sectionId,
      projectId: +projectId,
    };
  }
}

class DetectedObjectsAdapter {
  list(data) {
    return {
      data: data.map((detectedObject) => ({
        ...detectedObject,
        lvItemId: detectedObject.boqItemId,
        position_description: detectedObject.positionDescription,
        dwg_rgb: detectedObject.color
          ? typeof detectedObject.color === "string"
            ? (() => {
                // Remove '#' if present
                const hex = detectedObject.color.replace("#", "");
                // Handle 3-digit hex by expanding
                const hexNorm =
                  hex.length === 3
                    ? hex
                        .split("")
                        .map((x) => x + x)
                        .join("")
                    : hex;
                const r = parseInt(hexNorm.substring(0, 2), 16);
                const g = parseInt(hexNorm.substring(2, 4), 16);
                const b = parseInt(hexNorm.substring(4, 6), 16);
                return [r, g, b];
              })()
            : detectedObject.color
          : [0, 0, 0],
        group_id: detectedObject.groupName,
        chessboard_id:
          detectedObject.gridCols && detectedObject.gridRows
            ? `[${detectedObject.gridCols.join(",")}],[${detectedObject.gridRows.join("-")}]`
            : null,
        _id: detectedObject.id,
      })),
      success: true,
    };
  }

  connectDetectedObjectsToLvByLayer(data) {
    return {
      floorPlanId: data.floorPlanId,
      groupName: data.groupId,
      boqItemId: data.lvItemId,
    };
  }

  create(data) {
    return {
      type: data.type,
      positionDescription: data.position_description,
      detectedAt: data.detectionDate,
      position: data.position,
      gridCols: data.grid_cols,
      gridRows: data.grid_rows,
      floorPlanId: data.floorPlanId,
    };
  }
  createResponse(data) {
    return {
      ...data,
      data: {
        ...data.data,
        _id: data.data.id,
      },
    };
  }
  chat(data) {
    return {
      projectId: +data.projectId,
      sectionId: +data.sectionId,
      message: data.message,
    };
  }
}

class OrganizationAdapter {
  getMyOrganization(data) {
    return {
      ...data,
      organization: {
        ...data.organization,
        _id: data.organization.id,
        organizationType: data.organization.type,
      },
    };
  }
  getOrganizationMembers(data) {
    return {
      ...data,
      members: data.members.map((member) => ({
        ...member,
        user: {
          ...member.user,
          _id: member.user.id,
          name: member.user.firstName + " " + (member.user.lastName || ""),
        },
      })),
      invitations: data.invitations.map((invitation) => ({
        ...invitation,
        _id: invitation.id,
        name: invitation.firstName + " " + (invitation.lastName || ""),
        invitedAt: invitation.createdAt,
        invitedBy: {
          ...invitation.invitedBy,
          _id: invitation.invitedBy.id,
          name:
            invitation.invitedBy.firstName +
            " " +
            (invitation.invitedBy.lastName || ""),
        },
      })),
    };
  }
  updateOrganization(data) {
    return {
      name: data.name,
      description: data.description,
      organizationType: data.type,
    };
  }
}

class Adapter {
  admin = new AdminAdapter();
  user = new UserAdapter();
  project = new ProjectAdapter();
  section = new SectionAdapter();
  lv = new LvAdapter();
  documentation = new DocumentationAdapter();
  detectedObjects = new DetectedObjectsAdapter();
  organization = new OrganizationAdapter();
}

const adapter = wrapAdapter(new Adapter());

export default adapter;
